import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiError } from '../types';
import { auth } from '../lib/firebase/config';
import { apiConfig } from './config';
import { getLogger } from '../lib/logger';

/**
 * Base API service with automatic token refresh and retry logic.
 *
 * Best Practices:
 * - Automatically attaches fresh tokens to requests
 * - Handles 401 errors by refreshing token and retrying once
 * - Queues concurrent requests during token refresh to avoid race conditions
 * - Only retries on 401 (unauthorized), not on other errors
 */
export abstract class BaseApiService {
  protected client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: apiConfig.baseURL,
      timeout: apiConfig.timeout || 20000, // 20 seconds
      headers: {
        'Content-Type': 'application/json',
        'X-App-Client-Name': 'volt-web',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor: Attach fresh token to every request
    this.client.interceptors.request.use(
      async (config) => {
        const user = auth.currentUser;
        if (user) {
          // Firebase SDK automatically refreshes expired tokens when getIdToken() is called
          // This ensures we always have a valid token
          try {
            const token = await user.getIdToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            const logger = getLogger();
            logger.error('Error getting ID token for request', {
              error: error instanceof Error ? error.message : String(error),
            });
            // Continue without token - will fail with 401 and trigger refresh
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor: Handle 401 errors with token refresh and retry
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        // Only handle 401 (Unauthorized) errors and only retry once
        if (status === 401 && originalRequest && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, queue this request to retry after refresh completes
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                // Retry the original request with new token
                return this.client(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          const user = auth.currentUser;
          if (user) {
            try {
              // Force refresh the token
              await user.getIdToken(true);
              // Process queued requests (they will retry with new token)
              this.processQueue(null);
              // Retry the original request with new token
              return this.client(originalRequest);
            } catch (refreshError) {
              // Token refresh failed - user may be logged out or token invalid
              const errorMessage =
                refreshError instanceof Error
                  ? refreshError.message
                  : 'Token refresh failed';
              const logger = getLogger();
              logger.error('Token refresh failed', {
                error: errorMessage,
              });

              // Reject all queued requests
              this.processQueue(
                refreshError instanceof Error
                  ? refreshError
                  : new Error(errorMessage)
              );

              // Sign out user as token is invalid
              try {
                await auth.signOut();
              } catch (signOutError) {
                const logger = getLogger();
                logger.error('Error during auto logout', {
                  error:
                    signOutError instanceof Error
                      ? signOutError.message
                      : String(signOutError),
                });
              }
            } finally {
              this.isRefreshing = false;
            }
          } else {
            // No user authenticated - clear queue and sign out
            this.isRefreshing = false;
            this.processQueue(new Error('No authenticated user'));
            try {
              await auth.signOut();
            } catch (signOutError) {
              const logger = getLogger();
              logger.error('Error during auto logout', {
                error:
                  signOutError instanceof Error
                    ? signOutError.message
                    : String(signOutError),
              });
            }
          }
        }

        // Normalize error response for all other errors
        const normalizedError = new ApiError(
          error.response?.data?.message ||
            error.response?.data?.detail ||
            error.response?.data?.error ||
            error.message ||
            'An unexpected error occurred',
          error.response?.status,
          error.response?.data
        );

        return Promise.reject(normalizedError);
      }
    );
  }

  /**
   * Process queued requests after token refresh.
   * Resolves all queued promises if refresh succeeded, rejects them if it failed.
   */
  private processQueue(error: Error | null): void {
    const queue = [...this.failedQueue];
    this.failedQueue = [];

    queue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve();
      }
    });
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  protected async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  protected async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  protected async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  protected async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}
